import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import MenuIcon from '@mui/icons-material/Menu'
import { Box } from '@mui/material'
import Divider from '@mui/material/Divider'
import Drawer from '@mui/material/Drawer'
import IconButton from '@mui/material/IconButton'
import { styled, useTheme, useColorScheme } from '@mui/material/styles'
import { Stack } from '@mui/system'
import { useRef, useState } from 'react'
import { NavLink, useParams } from 'react-router-dom'
import { useLocation } from '../../api/apiQueries'
import AddressChangeSchedulerIcon from '../../assets/images/icons/address-change-scheduler-icon.svg?react'
import AddressSchedulerIcon from '../../assets/images/address-scheduler-icon.svg?react'
import AlarmSmsReviewIcon from '../../assets/images/icons/alarm-sms-review-icon.svg?react'
import IntrusionAlarmReviewIcon from '../../assets/images/icons/intrusion-alarm-review-icon.svg?react'
import MonitoringModerationIcon from '../../assets/images/icons/monitoring-moderation-icon.svg?react'
import SimplisafeLogoDarkMode from '../../assets/images/simplisafe-logo-wordmark-dark-mode.svg'
import SimplisafeLogoLightMode from '../../assets/images/simplisafe-logo-wordmark-light-mode.svg'

import { LocationCard, LocationCardPopper } from '../LocationCard'
import { UserProfileCard } from '../UserProfileCard'
import { NavItem } from './NavItem'
import { getSideNavExpanded, setSideNavExpanded } from '../../utils/localStorageUtils'

const expandedDrawerSize = '25rem'
const collapsedDrawerSize = '5.5rem'

const DrawerHeader = styled('div')(({ theme }) => ({
  ...theme.mixins.toolbar,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  columnGap: 'var(--spacing-s)',
  paddingBlock: 'var(--spacing-m)',
  paddingInline: 'var(--spacing-s)'
}))

export default function SideNav() {
  const theme = useTheme()
  const [isExpanded, setIsExpanded] = useState(getSideNavExpanded())
  const setExpanded = (expanded: boolean) => {
    setIsExpanded(expanded)
    setSideNavExpanded(expanded)
  }

  const [showPopper, setShowPopper] = useState(false)
  const { sid: locationId } = useParams()
  const locationQuery = useLocation(locationId)
  const location = locationQuery.data
  const locationCardRef = useRef<HTMLDivElement>(null)
  const hidePopperTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const { mode } = useColorScheme()

  const simplisafeLogoUrl = mode === 'light' ? SimplisafeLogoLightMode : SimplisafeLogoDarkMode

  const buildNavItemHref = (navItemPath: string) => {
    if (locationId !== undefined) {
      return `/locations/${locationId}/${navItemPath}` as const
    } else {
      return `/locations/${navItemPath}` as const
    }
  }

  const handleDrawerStateChange = () => {
    setExpanded(!isExpanded)
  }

  const handleLocationCardMouseEnter = () => {
    if (hidePopperTimeoutRef.current) {
      clearTimeout(hidePopperTimeoutRef.current)
      hidePopperTimeoutRef.current = null
    }
    setShowPopper(true)
  }

  const handleLocationCardMouseLeave = () => {
    // a delay before hiding the popper
    hidePopperTimeoutRef.current = setTimeout(() => {
      setShowPopper(false)
      hidePopperTimeoutRef.current = null
    }, 500)
  }

  return (
    <Drawer
      variant="persistent"
      anchor="left"
      open={true}
      slotProps={{
        paper: {
          elevation: 2
        }
      }}
      sx={{
        width: isExpanded ? expandedDrawerSize : collapsedDrawerSize,
        flexShrink: 0,
        transition: theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen
        }),
        '& .MuiDrawer-paper': {
          width: isExpanded ? expandedDrawerSize : collapsedDrawerSize,
          overflowX: 'hidden',
          borderRight: 'none',
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen
          })
        }
      }}
    >
      <Stack
        sx={{
          paddingInline: 'var(--spacing-2xs)'
        }}
      >
        <DrawerHeader
          sx={{
            paddingInlineStart: isExpanded ? 'var(--spacing-m)' : undefined,
            paddingInlineEnd: isExpanded ? 'var(--spacing-s)' : undefined,
            transition: theme.transitions.create('padding', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen
            })
          }}
        >
          <NavLink
            to="/locations"
            style={{
              display: isExpanded ? 'flex' : 'none',
              opacity: isExpanded ? 1 : 0,
              alignItems: 'center',
              transition: `
                  display ${theme.transitions.duration.leavingScreen}ms ${theme.transitions.easing.sharp} allow-discrete,
                  opacity ${theme.transitions.duration.leavingScreen}ms ${theme.transitions.easing.sharp}
                `
            }}
          >
            <img src={simplisafeLogoUrl} alt="SimpliSafe logo" />
          </NavLink>
          <IconButton
            title={isExpanded ? 'Collapse drawer' : 'Expand drawer'}
            aria-label={isExpanded ? 'Collapse drawer' : 'Expand drawer'}
            onClick={handleDrawerStateChange}
          >
            {isExpanded ? <ChevronLeftIcon /> : <MenuIcon />}
          </IconButton>
        </DrawerHeader>
        <Divider />
      </Stack>

      <Box ref={locationCardRef} sx={{ padding: 'var(--spacing-m) var(--spacing-2xs) var(--spacing-3xs) var(--spacing-2xs)' }}>
        <LocationCard
          location={location}
          microView={!isExpanded}
          onMouseEnter={handleLocationCardMouseEnter}
          onMouseLeave={handleLocationCardMouseLeave}
        />
        {!isExpanded && location && (
          <LocationCardPopper
            location={location}
            open={showPopper}
            anchorRef={locationCardRef}
            onMouseEnter={() => {
              if (hidePopperTimeoutRef.current) {
                clearTimeout(hidePopperTimeoutRef.current)
                hidePopperTimeoutRef.current = null
              }
            }}
            onMouseLeave={handleLocationCardMouseLeave}
          />
        )}
      </Box>

      <NavItem
        testId="location-management-tab"
        isOpen={isExpanded}
        to={buildNavItemHref('location-management')}
        label={'Location Management'}
        icon={<AddressSchedulerIcon style={{ width: 'var(--icons-m)', height: 'var(--icons-m)' }} />}
      />
      <NavItem
        testId="alarm-sms-tab"
        isOpen={isExpanded}
        to={buildNavItemHref('alarm-sms-review')}
        label={'Alarm SMS Review'}
        icon={<AlarmSmsReviewIcon style={{ width: 'var(--icons-m)', height: 'var(--icons-m)' }} />}
      />
      <NavItem
        testId="engagement-review-tab"
        isOpen={isExpanded}
        to={buildNavItemHref('engagement-review')}
        label={'Engagement Review Tool'}
        icon={<IntrusionAlarmReviewIcon style={{ width: 'var(--icons-m)', height: 'var(--icons-m)' }} />}
      />
      <NavItem
        testId="address-scheduler-tab"
        isOpen={isExpanded}
        to={buildNavItemHref('address-scheduler')}
        label={'Address Scheduler'}
        icon={<AddressChangeSchedulerIcon style={{ width: 'var(--icons-m)', height: 'var(--icons-m)' }} />}
      />
      <NavItem
        testId="monitoring-moderation-tab"
        isOpen={isExpanded}
        to={buildNavItemHref(locationId ? 'monitoring-moderation/camera-overview' : 'monitoring-moderation')}
        label={'Monitoring Moderation'}
        icon={<MonitoringModerationIcon style={{ width: 'var(--icons-m)', height: 'var(--icons-m)' }} />}
      />
      {/* <NavItem
            testId="devtools-tab"
            isOpen={isExpanded}
            hasScopes={ALL_DEVTOOLS_SCOPES}
            label={ALL_APPLET_NAMES.DEVTOOLS}
            icon={<CodeIcon style={{ width: 'var(--icons-m)', height: 'var(--icons-m)' }} />}
          >
          // This location features has been moved but I'm leaving this here as an example of how to nest a subnav under the parent NavItem
          <NavItem
            testId="location-features-tab"
            isOpen={isExpanded}
            hasScopes={LOCATION_FEATURES_APPLET_SCOPES}
            to={buildNavItemHref('location-features')}
            label={ALL_APPLET_NAMES.LOCATION_FEATURE_VIEWER}
          />
        </NavItem> */}
      <UserProfileCard isExpanded={isExpanded} />
    </Drawer>
  )
}
